import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnInit,
  signal,
  Signal,
  viewChildren,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { ActivatedRoute, type Params, Router } from '@angular/router';

import { map, Observable, of, take } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ElementActions, ElementTypes, GaContext, MediaQueryService, SchemaService, ScrollService } from '@fcom/common';
import { GtmService } from '@fcom/common/gtm';
import { CmsDataService, ConfigService, sanitizeWhiteListedPath, stopPropagation } from '@fcom/core';
import { type CmsPlaceholder, type CmsTeaserCard } from '@fcom/core-api';
import { ButtonMode, ButtonTheme, ChipTheme, IconPosition, LoaderTheme, PopoverService } from '@fcom/ui-components';
import { LanguageService, LocalizationPipe } from '@fcom/ui-translate';

import {
  BluewingsContentLocalSettings,
  type BluewingsFiltersItem,
  BluewingsFilterType,
  BluewingsFilterTypeContinent,
  BluewingsQueryParam,
  COUNTRY_CODE,
  DEFAULT_POPOVER_OPTIONS,
  ItemStatus,
  type SearchHit,
} from '../bluewings.interface';

@Component({
  selector: 'fin-bluewings-main-page',
  templateUrl: './bluewings-search-api-main-page.component.html',
  styleUrl: './bluewings-search-api-main-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BluewingsMainPageComponent implements OnInit {
  readonly AspectRatio = '16x9';
  readonly BluewingsFilterType = BluewingsFilterType;
  readonly ButtonMode = ButtonMode;
  readonly ButtonTheme = ButtonTheme;
  readonly ChipTheme = ChipTheme;
  readonly IconPosition = IconPosition;
  readonly ItemStatus = ItemStatus;
  readonly LoaderTheme = LoaderTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly BluewingsFilterTypeContinent = BluewingsFilterTypeContinent;
  readonly COUNTRY_CODE = COUNTRY_CODE;

  private activatedRoute = inject(ActivatedRoute);
  private configService = inject(ConfigService);
  private cmsDataService = inject(CmsDataService);
  private languageService = inject(LanguageService);
  private localization = inject(LocalizationPipe);
  private mediaQueryService = inject(MediaQueryService);
  private popoverService = inject(PopoverService);
  private router = inject(Router);
  private schemaService = inject(SchemaService);
  private document = inject<Document>(DOCUMENT);
  private scrollService = inject(ScrollService);
  private gtmService = inject(GtmService);
  private destroyRef = inject(DestroyRef);

  popoverOptions$ = of(DEFAULT_POPOVER_OPTIONS);

  content: InputSignal<CmsPlaceholder> = input<CmsPlaceholder>();

  listItems: Signal<readonly ElementRef[]> = viewChildren('listItem', { read: ElementRef });

  activeContinent: WritableSignal<string> = signal<string>('');
  activeFilters: WritableSignal<BluewingsFiltersItem[]> = signal<BluewingsFiltersItem[]>([]);
  continents: WritableSignal<BluewingsFiltersItem[]> = signal<BluewingsFiltersItem[]>([]);
  continentsWithSelections: WritableSignal<string[]> = signal<string[]>([]);
  destinations: WritableSignal<BluewingsFiltersItem[]> = signal<BluewingsFiltersItem[]>([]);
  isModalOpen: WritableSignal<boolean> = signal<boolean>(false);
  items$: WritableSignal<Observable<CmsTeaserCard[]>> = signal<Observable<CmsTeaserCard[]>>(of([]));
  loadedItems: WritableSignal<CmsTeaserCard[]> = signal<CmsTeaserCard[]>([]);
  locationList: WritableSignal<string[]> = signal<string[]>([]);
  locationListFilters: WritableSignal<string[]> = signal<string[]>([]);
  offset: WritableSignal<number> = signal<number>(0);
  pageNumber: WritableSignal<number> = signal(1);
  numberOfPages: WritableSignal<number> = signal<number>(1); // Tracks total pages available for the current result set.
  subjectList: WritableSignal<string[]> = signal<string[]>([]);
  total: WritableSignal<number> = signal<number>(0);
  subjects: WritableSignal<BluewingsFiltersItem[]> = signal<BluewingsFiltersItem[]>([]);

  itemsLoader: WritableSignal<boolean> = signal<boolean>(true);
  itemsLoadingStatus: WritableSignal<ItemStatus> = signal<ItemStatus>(ItemStatus.INITIAL);

  activeCountryCount: Signal<number> = computed(
    (): number => this.destinations().filter((item: BluewingsFiltersItem): boolean => item.isActive).length
  );
  activeFilters$ = toObservable(this.activeFilters);
  fetchMore$: Signal<Observable<CmsTeaserCard[]>> = computed((): Observable<CmsTeaserCard[]> => {
    const searchHits: Observable<SearchHit> = this.getListOfItems(this.offset());

    return searchHits.pipe(
      map((response): CmsTeaserCard[] => {
        this.schemaBuilder(response.articles);
        return response.articles;
      }),
      take(1)
    );
  });
  filters = computed(
    (): BluewingsContentLocalSettings =>
      this.content().localSettings[BluewingsContentLocalSettings.FILTERS] as BluewingsContentLocalSettings
  );
  hasFiltering: Signal<boolean> = computed((): boolean => this.activeCountryCount() + this.countActive() > 0);
  isMobile$: Signal<Observable<boolean>> = computed(
    (): Observable<boolean> => this.mediaQueryService.isBreakpoint$('mobile')
  );
  limit: Signal<number> = computed(
    (): number => (this.content().localSettings[BluewingsContentLocalSettings.LIMIT] as number | undefined) ?? 12
  );
  parsedPageNum: Signal<number> = computed(
    (): number => this.router.parseUrl(this.path()).queryParams[BluewingsQueryParam.PAGE] ?? 1
  );
  parsedOffset: Signal<number> = computed((): number =>
    this.parsedPageNum() ? (this.parsedPageNum() - 1) * this.limit() : null
  );
  path: Signal<string> = computed((): string => sanitizeWhiteListedPath(this.router.url, this.configService.cfg));
  schemaTitle: Signal<string> = computed((): string => this.localization.transform('blueWingsSEO.schemaTitle'));
  schemaDescription: Signal<string> = computed((): string =>
    this.localization.transform('blueWingsSEO.schemaDescription')
  );
  strippedUrl: Signal<string> = computed((): string =>
    this.path()
      .replace(/[?&]pageNum=\d+/, '')
      .replace(/[?&]dest=\d+/, '')
      .replace(/[?&]subject=\d+/, '')
  );
  urlDelimiter: Signal<string> = computed((): '?' | '&' => (this.strippedUrl().includes('?') ? '&' : '?'));
  nextPageLink: Signal<string> = computed((): string => {
    if (this.total() <= this.limit()) {
      return null;
    }

    if (this.pageNumber() >= this.numberOfPages()) {
      return null;
    }

    return `${this.strippedUrl()}${this.urlDelimiter()}pageNum=${Number(this.pageNumber() + 1)}`;
  });
  readonly worldOfFinnairFilterType: Signal<BluewingsFilterType> = computed((): BluewingsFilterType => {
    const filters: BluewingsContentLocalSettings = this.filters();
    return filters?.[BluewingsFilterType.WORLD_OF_FINNAIR]
      ? BluewingsFilterType.WORLD_OF_FINNAIR
      : BluewingsFilterType.FINNAIR_STORIES;
  });

  constructor() {
    effect((): void => {
      this.numberOfPages.update((): number => Math.ceil(this.total() / this.limit()));

      this.continentsWithSelections.update((): string[] =>
        Object.keys(this.filters()[BluewingsFilterType.CONTINENT]).filter((key: string): boolean =>
          this.destinations().some(
            (d: BluewingsFiltersItem): boolean => d.value !== COUNTRY_CODE.FI && d.continent === key && d.isActive
          )
        )
      );
    });
  }

  ngOnInit(): void {
    const locationParams = this.activatedRoute.snapshot.queryParams?.[BluewingsQueryParam.DESTINATIONS];
    const subjectParams = this.activatedRoute.snapshot.queryParams?.[BluewingsQueryParam.SUBJECT];

    this.initialFilters();
    this.offset.set(
      locationParams || subjectParams ? 0 : this.parsedPageNum() ? (this.parsedPageNum() - 1) * this.limit() : 0
    );

    // set the page number based on the parsed number on init.
    this.pageNumber.set(this.parsedPageNum());

    this.activeFilters$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((filters): void => {
      if (!filters.length) {
        if (this.pageNumber() === 1) {
          // remove the pageNum when all filters have been removed
          this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: { [BluewingsQueryParam.PAGE]: null },
            queryParamsHandling: 'merge',
          });
        }
        this.unfilteredSearch(this.offset());
        return;
      }
      this.itemsLoadingStatus.set(ItemStatus.INITIAL_LOAD);
      this.itemsLoader.set(true);

      this.items$.update((): Observable<CmsTeaserCard[]> => {
        const searchHits: Observable<SearchHit> = this.getListOfItems(0);

        return searchHits.pipe(
          map((response): CmsTeaserCard[] => {
            if (response.numHits < 1) {
              this.itemsLoadingStatus.set(ItemStatus.EMPTY);
              this.itemsLoader.set(false);
              this.total.set(0);
              return null;
            }

            this.itemsLoadingStatus.set(ItemStatus.LOADED);
            this.itemsLoader.set(false);
            this.total.set(response.numHits);
            this.schemaBuilder(response.articles);

            return response.articles;
          })
        );
      });
    });
  }

  getFinland(): BluewingsFiltersItem | undefined {
    return this.destinations().find((d): boolean => d.value === COUNTRY_CODE.FI);
  }

  onContinentSelection(value: string): boolean {
    return this.continentsWithSelections().includes(value);
  }

  private getListOfItems(
    offset: number,
    locationList = this.locationList(),
    subjectList = this.subjectList()
  ): Observable<SearchHit> {
    return this.cmsDataService.getArticleListByTaxonomyTags({
      lang: this.languageService.langValue,
      locale: this.languageService.localeValue,
      taxonomyTags: this.content().subjectTaxonomyTags,
      filter: subjectList,
      locations: locationList,
      limit: this.limit(),
      offset,
      softFiltering: true,
    });
  }

  private unfilteredSearch(offset: number): void {
    const searchHits: Observable<SearchHit> = this.getListOfItems(offset);

    this.itemsLoadingStatus.set(ItemStatus.INITIAL_LOAD);
    this.itemsLoader.set(true);

    this.items$.update((): Observable<CmsTeaserCard[]> => {
      return searchHits.pipe(
        map((response): CmsTeaserCard[] => {
          if (response.numHits < 1) {
            this.itemsLoadingStatus.set(ItemStatus.EMPTY);
            this.itemsLoader.set(false);
            this.total.set(0);
            return null;
          }

          this.itemsLoadingStatus.set(ItemStatus.LOADED);
          this.itemsLoader.set(false);
          this.total.set(response.numHits);
          this.schemaBuilder(response.articles);

          return response.articles;
        })
      );
    });
  }

  protected schemaBuilder(list: CmsTeaserCard[] = []): void {
    if (this.activeFilters().length) {
      // empty schemas when filtering is active
      this.schemaService.clearSchemas();
      return;
    }

    const pageNumberParam: string = this.pageNumber() === 1 ? '' : `?pageNum=${Number(this.pageNumber())}`;

    this.schemaService.addBlueWingsTeaserCardSchema({
      name: this.pageNumber() > 1 ? `${this.schemaTitle()} - Page ${this.pageNumber()}` : this.schemaTitle(),
      description: this.schemaDescription(),
      id: `${this.document.location.origin}${this.strippedUrl()}${pageNumberParam}`,
      language: this.languageService.localeValue,
      list,
      limit: this.limit(),
    });
  }

  private initialFilters(): void {
    const locationParams = this.activatedRoute.snapshot.queryParams?.[BluewingsQueryParam.DESTINATIONS];
    const subjectParams = this.activatedRoute.snapshot.queryParams?.[BluewingsQueryParam.SUBJECT];
    const activeLocations: string[] = typeof locationParams === 'string' ? locationParams.split(',') : [];
    const activeSubjects: string[] = typeof subjectParams === 'string' ? subjectParams.split(',') : [];

    if (locationParams) {
      this.locationList.update((locations: string[]): string[] => {
        let updatedLocations: string[] = [...locations];
        activeLocations.forEach((item): void => {
          if (updatedLocations.includes(item)) {
            updatedLocations = updatedLocations.filter((i: string): boolean => i !== item);
          } else {
            updatedLocations = [...updatedLocations, item];
          }
        });
        return updatedLocations;
      });
    }

    this.continents.update((): BluewingsFiltersItem[] =>
      Object.keys(this.filters()[BluewingsFilterType.CONTINENT])
        .map(
          (key: string): BluewingsFiltersItem => ({
            name: this.localization.transform('blueWingsSEO.continents.' + key),
            isActive: false,
            value: key,
          })
        )
        .sort((a: BluewingsFiltersItem, b: BluewingsFiltersItem): number => a.name.localeCompare(b.name))
    );

    this.destinations.update((): BluewingsFiltersItem[] => {
      return Object.values(BluewingsFilterTypeContinent)
        .flatMap((continent: BluewingsFilterTypeContinent): BluewingsFiltersItem[] =>
          this.filters()[BluewingsFilterType.CONTINENT]?.[continent] ? this.translateFilters(continent, true) : []
        )
        .map(
          (item: BluewingsFiltersItem): BluewingsFiltersItem =>
            activeLocations.includes(item.value) ? { ...item, isActive: true } : item
        )
        .sort((a: BluewingsFiltersItem, b: BluewingsFiltersItem): number => a.name.localeCompare(b.name));
    });

    this.subjects.update((): BluewingsFiltersItem[] => {
      return [
        ...(this.filters()[BluewingsFilterType.WORLD_OF_FINNAIR]
          ? this.translateFilters(BluewingsFilterType.WORLD_OF_FINNAIR)
          : this.translateFilters(BluewingsFilterType.FINNAIR_STORIES)), // currently it's FINNAIR_STORIES, will be WORLD_OF_FINNAIR at a later date, this accounts for that change.
        ...this.translateFilters(BluewingsFilterType.SEASONALITY),
        ...this.translateFilters(BluewingsFilterType.TRAVEL_EXPERIENCE),
        ...this.translateFilters(BluewingsFilterType.TRAVEL_TIPS),
      ]
        .map(
          (item: BluewingsFiltersItem): BluewingsFiltersItem =>
            activeSubjects.includes(item.value) ? { ...item, isActive: true } : item
        )
        .sort((a: BluewingsFiltersItem, b: BluewingsFiltersItem): number => a.name.localeCompare(b.name));
    });
    this.renderFilters(true);
  }

  private translateFilters(
    source: BluewingsFilterType | BluewingsFilterTypeContinent,
    destinations = false
  ): BluewingsFiltersItem[] {
    if (destinations) {
      return this.filters()[BluewingsFilterType.CONTINENT][source].map(
        (value: string): BluewingsFiltersItem => ({
          name: this.localization.transform('blueWingsSEO.countries.' + value),
          continent: source as BluewingsFilterTypeContinent,
          value: value,
          type: BluewingsFilterType.DESTINATIONS,
          isActive: false,
        })
      );
    }

    return this.filters()[source].map(
      (value: string): BluewingsFiltersItem => ({
        name: this.localization.transform('blueWingsSEO.tags.' + value),
        value: value,
        type: source as BluewingsFilterType,
        isActive: false,
      })
    );
  }

  private updateFilterActiveState(
    filter: string,
    typeOrContinent: BluewingsFilterType | BluewingsFilterTypeContinent,
    filterList: WritableSignal<BluewingsFiltersItem[]>,
    destinations = false
  ): void {
    filterList.update((list: BluewingsFiltersItem[]): BluewingsFiltersItem[] =>
      list.map((item: BluewingsFiltersItem): BluewingsFiltersItem => {
        const itemType: BluewingsFilterType | BluewingsFilterTypeContinent = destinations ? item.continent : item.type;
        return item.value === filter && itemType === typeOrContinent ? { ...item, isActive: !item.isActive } : item;
      })
    );
  }

  private resetContinentState(): void {
    this.activeContinent.set(null);
    this.continents.update((c: BluewingsFiltersItem[]): BluewingsFiltersItem[] => {
      return c.map(
        (i): BluewingsFiltersItem => ({
          ...i,
          isActive: false,
        })
      );
    });
  }

  private renderFilters(init = false): void {
    if (init) {
      this.subjectList.update((): string[] => {
        return this.subjects()
          .filter((item): boolean => item.isActive)
          .map((item): string => item.value);
      });
    }

    const subjectlist: BluewingsFiltersItem[] = this.subjects().filter(
      (item: BluewingsFiltersItem): boolean => item.isActive
    );
    const locationlist: BluewingsFiltersItem[] = this.destinations().filter(
      (item: BluewingsFiltersItem): boolean => item.isActive
    );

    this.activeFilters.set([...subjectlist, ...locationlist]);
    this.loadedItems.set([]);
    this.offset.set(0);
    this.pageNumber.set(1);
  }

  private updateQueryParams(): void {
    const queryParams: Params = {
      [BluewingsQueryParam.PAGE]: this.pageNumber() <= this.numberOfPages() ? this.pageNumber() : null,
    };

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  showContinents(continent: string): void {
    this.continents.update((c: BluewingsFiltersItem[]): BluewingsFiltersItem[] => {
      this.activeContinent.set(continent);
      return c.map(
        (i: BluewingsFiltersItem): BluewingsFiltersItem => ({
          ...i,
          isActive: i.value === continent,
        })
      );
    });
  }

  updateFiltersList(filter: string, type: BluewingsFilterType, continent?: BluewingsFilterTypeContinent): void {
    if (type === BluewingsFilterType.DESTINATIONS) {
      this.updateFilterActiveState(filter, continent, this.destinations, true);
    } else {
      this.updateFilterActiveState(filter, type, this.subjects);
    }
  }

  removeFilter(filter: string, type: BluewingsFilterType, continent?: BluewingsFilterTypeContinent): void {
    if (type === BluewingsFilterType.DESTINATIONS) {
      this.updateFilterActiveState(filter, continent, this.destinations, true);
      this.locationList.update((locations): string[] => locations.filter((i): boolean => i !== filter));
    } else {
      this.subjectList.update((subjects: string[]): string[] => {
        return subjects.filter((i): boolean => i !== filter);
      });
      this.updateFilterActiveState(filter, type, this.subjects);
    }

    this.itemsLoadingStatus.set(ItemStatus.LOADING);
    this.itemsLoader.set(true);
    this.renderFilters();
  }

  checkIfActive(value: string, list: string[]): boolean {
    return list.some((filter): boolean => filter === value);
  }

  loadMore(event: Event): void {
    stopPropagation(event);

    this.itemsLoadingStatus.set(ItemStatus.LOAD_MORE);
    this.itemsLoader.set(true);

    this.offset.update((value): number => value + this.limit());
    this.pageNumber.update((value: number): number => Number(value) + 1);
    this.updateQueryParams();

    this.fetchMore$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((list): void => {
        this.itemsLoadingStatus.set(ItemStatus.LOADED);
        this.itemsLoader.set(false);

        this.loadedItems.update((current: CmsTeaserCard[]): CmsTeaserCard[] => {
          return [...current, ...list];
        });
      });

    this.loadMoreFocus();
    this.trackElement('load-more');
  }

  private loadMoreFocus(): void {
    const focusIndex: number = this.listItems().length - 1;
    const focusElement: HTMLElement = this.listItems()[focusIndex]?.nativeElement.querySelector('a');
    if (!this.loadedItems().length) {
      // stop scroll jump to top of the page on the first load more click
      this.scrollService.smoothScroll(focusElement, { offsetY: 120 });
    }
    setTimeout((): void => {
      this.listItems()[focusIndex]?.nativeElement.querySelector('a')?.focus();
    });
  }

  applyFiltersAndClose(type = '', modal = false): void {
    this.itemsLoadingStatus.set(ItemStatus.LOADING);
    this.itemsLoader.set(true);

    if (modal) {
      this.locationList.update((): string[] =>
        this.destinations()
          .filter((item: BluewingsFiltersItem): boolean => item.isActive)
          .map((item: BluewingsFiltersItem): string => item.value)
      );
      this.subjectList.update((): string[] => {
        return this.subjects()
          .filter((item: BluewingsFiltersItem): boolean => item.isActive)
          .map((item: BluewingsFiltersItem): string => item.value);
      });
      this.resetContinentState();
      this.renderFilters();
      this.closeModal();
      return;
    }

    if (type === BluewingsFilterType.DESTINATIONS) {
      this.locationList.update((): string[] =>
        this.destinations()
          .filter((item: BluewingsFiltersItem): boolean => item.isActive)
          .map((item: BluewingsFiltersItem): string => item.value)
      );
      this.resetContinentState();
      this.renderFilters();
    } else {
      this.subjectList.update((): string[] => {
        return this.subjects()
          .filter((item): boolean => item.isActive)
          .map((item): string => item.value);
      });
      this.renderFilters();
    }

    this.trackElement('apply-filters-' + type);
    this.popoverService.close(true);
  }

  private clearDestinationsState(): void {
    return this.destinations.update((d: BluewingsFiltersItem[]): BluewingsFiltersItem[] =>
      d.map((item: BluewingsFiltersItem): BluewingsFiltersItem => ({ ...item, isActive: false }))
    );
  }

  clearFilter(type: BluewingsFilterType): void {
    this.itemsLoadingStatus.set(ItemStatus.LOADING);
    this.itemsLoader.set(true);

    if (type === BluewingsFilterType.DESTINATIONS) {
      if (this.locationList().length) {
        this.clearDestinationsState();
        this.locationList.set([]);
        this.resetContinentState();
        this.renderFilters();
      } else {
        this.clearDestinationsState();
        this.itemsLoadingStatus.set(ItemStatus.LOADED);
        this.itemsLoader.set(false);
      }
      this.activeContinent.set('');
    } else {
      this.subjects.update((list: BluewingsFiltersItem[]): BluewingsFiltersItem[] =>
        list.map(
          (item: BluewingsFiltersItem): BluewingsFiltersItem =>
            item.type === type ? { ...item, isActive: false } : item
        )
      );
      this.subjectList.update((): string[] => {
        return this.subjects()
          .filter((item): boolean => item.isActive)
          .map((item): string => item.value);
      });
      this.renderFilters();
    }

    this.trackElement('clear-filters-' + type);
    this.popoverService.close(true);
  }

  trackElement(label: string): void {
    this.gtmService.trackElement(label, GaContext.CONTENT, ElementTypes.BUTTON, undefined, ElementActions.CLICK);
  }

  countActive(type: BluewingsFilterType = null, continent: BluewingsFilterTypeContinent = null): number {
    if (type === BluewingsFilterType.DESTINATIONS) {
      return this.destinations().filter((item): boolean => item.continent === continent && item.isActive).length;
    }

    return type
      ? this.subjects().filter((item): boolean => item.type === type && item.isActive).length
      : this.subjects().filter((item): boolean => item.isActive).length;
  }

  countActiveCountries(count = true, continent: string = null): number | boolean {
    if (count && continent) {
      return this.destinations().filter(
        (item: BluewingsFiltersItem): boolean => item.continent === continent && item.isActive
      ).length;
    }

    if (count) {
      return this.destinations().filter((item: BluewingsFiltersItem): boolean => item.isActive).length;
    }

    return this.destinations().some((item: BluewingsFiltersItem): boolean => item.isActive);
  }

  showModal(): void {
    this.isModalOpen.set(true);
  }

  closeModal(): void {
    this.resetContinentState();
    this.isModalOpen.set(false);
  }

  clearAllFilters(): void {
    this.itemsLoadingStatus.set(ItemStatus.LOADING);
    this.itemsLoader.set(true);

    if (this.destinations().some((d: BluewingsFiltersItem): boolean => d.isActive)) {
      this.destinations.update((list: BluewingsFiltersItem[]): BluewingsFiltersItem[] =>
        list.map(
          (item: BluewingsFiltersItem): BluewingsFiltersItem => (item.isActive ? { ...item, isActive: false } : item)
        )
      );
      this.locationList.set([]);
    }

    this.subjects.update((list: BluewingsFiltersItem[]): BluewingsFiltersItem[] =>
      list.map(
        (item: BluewingsFiltersItem): BluewingsFiltersItem => (item.isActive ? { ...item, isActive: false } : item)
      )
    );
    this.subjectList.set([]);

    this.resetContinentState();
    this.renderFilters();
    this.popoverService.close(true);
  }
}
